/**
 * Estrelas de avaliação, em duas formas.
 *
 * `Stars` mostra a nota; `StarsInput` deixa escolher.
 *
 * O preenchimento é fracionário: 4,5 desenha meia estrela, e não cinco cheias
 * por arredondamento. Numa média isso importa — arredondar para cima faz a
 * loja anunciar uma nota que ela não tem.
 *
 * A técnica é duas camadas sobrepostas: a de baixo com os contornos, a de cima
 * com as estrelas cheias, recortada na largura da nota. Por isso as estrelas
 * não usam `gap`: a folga vem de margem dentro de cada caixa, para que todas
 * ocupem exatamente a mesma largura e a porcentagem caia no ponto certo.
 */

function Estrela({ cheia }: { cheia: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill={cheia ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        d="M12 2.8l2.8 5.9 6.2.9-4.5 4.5 1.1 6.4-5.6-3.1-5.6 3.1 1.1-6.4L3 9.6l6.2-.9z"
      />
    </svg>
  );
}

const CINCO = [0, 1, 2, 3, 4];

export function Stars({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' | 'lg' }) {
  const nota = Math.min(5, Math.max(0, value));
  return (
    <span className={`estrelas ${size}`} role="img" aria-label={`Nota ${nota.toFixed(1).replace('.', ',')} de 5`}>
      <span className="estrelas-base" aria-hidden>
        {CINCO.map((n) => (
          <Estrela key={n} cheia={false} />
        ))}
      </span>
      <span className="estrelas-fill" style={{ width: `${(nota / 5) * 100}%` }} aria-hidden>
        {CINCO.map((n) => (
          <Estrela key={n} cheia />
        ))}
      </span>
    </span>
  );
}

const ROTULOS: Record<number, string> = {
  1: 'Péssimo',
  2: 'Ruim',
  3: 'Regular',
  4: 'Bom',
  5: 'Excelente',
};

export function StarsInput({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
}) {
  function teclado(e: React.KeyboardEvent) {
    const passo =
      e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -1 : 0;
    if (!passo) return;
    e.preventDefault();
    onChange(Math.min(5, Math.max(1, (value || 0) + passo)));
  }

  return (
    <div className="estrelas-input" role="radiogroup" aria-label="Sua nota" onKeyDown={teclado}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} — ${ROTULOS[n]}`}
          title={ROTULOS[n]}
          disabled={disabled}
          // Só a estrela marcada fica na ordem de tabulação; as setas fazem o
          // resto. É como um grupo de rádio se comporta nativamente.
          tabIndex={value === n || (!value && n === 1) ? 0 : -1}
          className={`estrelas-input-item ${n <= value ? 'on' : ''}`}
          onClick={() => onChange(n)}
        >
          <Estrela cheia={n <= value} />
        </button>
      ))}
      <span className="estrelas-rotulo">{value ? ROTULOS[value] : 'Escolha de 1 a 5'}</span>
    </div>
  );
}
